import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, ChevronUp, ChevronDown, GripVertical, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/shared";
import { BLOCK_LABELS, ADDABLE, Field, IconBtn, BlockEditor } from "@/pages/ChapterEditor";

const PAGE_META = {
  "start-here": { label: "Start Here", viewPath: "/app/start-here" },
  "conclusion": { label: "Conclusion", viewPath: "/app/conclusion" },
};

export default function PageEditor() {
  const { pageKey } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const meta = PAGE_META[pageKey] || { label: pageKey, viewPath: "/app" };

  useEffect(() => {
    if (user && !user.is_admin) return;
    api.get(`/admin/page/${pageKey}`).then((r) => setData(r.data)).catch(() => {});
  }, [pageKey, user]);

  if (user && !user.is_admin) return <div className="text-nm-neutral">Admin access required.</div>;
  if (!data) return <Loading label="Loading editor…" />;

  const setField = (k, v) => setData({ ...data, [k]: v });
  const setBlocks = (blocks) => setData({ ...data, blocks });
  const updateBlock = (i, patch) => setBlocks(data.blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= data.blocks.length) return;
    const arr = [...data.blocks];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setBlocks(arr);
  };
  const deleteBlock = (i) => setBlocks(data.blocks.filter((_, idx) => idx !== i));
  const addBlock = (t) => {
    const nb = t === "ul" || t === "ol" ? { t, items: [] }
      : t === "groups" ? { t, items: [{ label: "", items: [] }] }
      : t === "callout" ? { t, tone: "start", title: "", text: "" }
      : { t, text: "" };
    setBlocks([...data.blocks, nb]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/page/${pageKey}`, { title: data.title, intro: data.intro, blocks: data.blocks });
      toast.success(`${meta.label} saved`);
      nav(meta.viewPath);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!window.confirm("Discard all edits and restore the original built-in content?")) return;
    await api.post(`/admin/page/${pageKey}/reset`);
    toast.success("Restored original content");
    nav(meta.viewPath);
  };

  const ta = "w-full rounded-xl border border-nm-primary/15 bg-white px-3.5 py-2.5 text-sm text-nm-ink focus:outline-none focus:ring-2 focus:ring-nm-primary/40 transition-shadow";

  return (
    <div className="max-w-4xl pb-24" data-testid="page-editor">
      <div className="flex items-center justify-between gap-3">
        <Link to={meta.viewPath} className="inline-flex items-center gap-1.5 text-sm text-nm-neutral hover:text-nm-primary transition-colors" data-testid="page-editor-back">
          <ArrowLeft size={15} /> Back to {meta.label}
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={resetToDefault} data-testid="page-editor-reset" className="inline-flex items-center gap-1.5 rounded-full border border-nm-primary/15 px-4 py-2 text-sm text-nm-neutral hover:text-nm-ink transition-colors">
            <RotateCcw size={14} /> Restore original
          </button>
          <button onClick={save} disabled={saving} data-testid="page-editor-save" className="inline-flex items-center gap-2 rounded-full bg-nm-coral px-5 py-2 text-sm font-medium text-white hover:brightness-105 disabled:opacity-60 transition-[filter]">
            <Save size={15} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-nm-coral mt-6">Editing · {meta.label}</div>
      <h1 className="font-serif text-3xl text-nm-ink mt-1">Page editor</h1>

      {/* Meta */}
      <div className="mt-6 space-y-4 rounded-2xl border border-nm-primary/12 bg-nm-surface p-6">
        <Field label="Page title"><input value={data.title} onChange={(e) => setField("title", e.target.value)} className={ta} data-testid="page-field-title" /></Field>
        <Field label="Intro (large text under the title)"><textarea value={data.intro} onChange={(e) => setField("intro", e.target.value)} rows={3} className={ta} data-testid="page-field-intro" /></Field>
      </div>

      {/* Blocks */}
      <div className="mt-6 space-y-4">
        {data.blocks.map((blk, i) => (
          <div key={i} className="rounded-2xl border border-nm-primary/12 bg-nm-surface p-4" data-testid={`page-block-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-nm-primary">
                <GripVertical size={15} className="text-nm-neutral/40" />
                <span className="text-xs font-semibold uppercase tracking-wide">{BLOCK_LABELS[blk.t] || blk.t}</span>
              </div>
              <div className="flex items-center gap-1">
                <IconBtn onClick={() => moveBlock(i, -1)} testid={`page-up-${i}`}><ChevronUp size={16} /></IconBtn>
                <IconBtn onClick={() => moveBlock(i, 1)} testid={`page-down-${i}`}><ChevronDown size={16} /></IconBtn>
                <IconBtn onClick={() => deleteBlock(i)} testid={`page-del-${i}`} danger><Trash2 size={15} /></IconBtn>
              </div>
            </div>
            <BlockEditor blk={blk} i={i} updateBlock={updateBlock} ta={ta} />
          </div>
        ))}
      </div>

      {/* Add block */}
      <div className="mt-6 rounded-2xl border border-dashed border-nm-primary/25 p-4">
        <div className="text-sm text-nm-neutral mb-3 flex items-center gap-2"><Plus size={15} /> Add a block</div>
        <div className="flex flex-wrap gap-2">
          {ADDABLE.map((t) => (
            <button key={t} onClick={() => addBlock(t)} data-testid={`page-add-${t}`}
              className="rounded-full border border-nm-primary/15 px-3.5 py-1.5 text-xs font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors">
              {BLOCK_LABELS[t]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
